/**
 * React Query hooks for Skills (admin & tenant scopes) + agent skill bindings.
 *
 * Sprint 131 Stream C.
 *
 * The `api` helper from `@/lib/api` automatically prefixes tenant paths
 * (e.g. `skills` → `tenants/{tid}/skills`). Admin-scoped paths are passed as
 * `admin/skills/...` so they bypass the tenant prefix.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  AgentSkillBinding,
  AgentSkillBindingCreate,
  AgentSkillBindingPatch,
  AgentSkillsScopeUpdate,
  Skill,
  SkillAsset,
  SkillAssetCreate,
  SkillCreate,
  SkillFacets,
  SkillHardDeleteRequest,
  SkillImportFormat,
  SkillImportPreview,
  SkillImportResponse,
  SkillLifecycleTransition,
  SkillListFilters,
  SkillListResponse,
  SkillScope,
  SkillScopePreviewResponse,
  SkillUpdate,
  SkillUsageResponse,
  SkillVersionListResponse,
  SkillRollbackRequest,
} from '../types';

// =============================================================================
// Query keys
// =============================================================================

const SKILLS_KEY = 'skills';
const SKILL_KEY = 'skill';
const SKILL_USAGE_KEY = 'skill-usage';
const AGENT_SCOPE_KEY = 'agent-skill-scope';
const AGENT_BINDINGS_KEY = 'agent-skill-bindings';
const AGENT_PREVIEW_KEY = 'agent-skill-preview';

// =============================================================================
// Helpers
// =============================================================================

function buildListQuery(f: SkillListFilters): string {
  const params = new URLSearchParams();
  if (f.tier) params.set('tier', f.tier);
  if (f.lifecycle) params.set('lifecycle', f.lifecycle);
  if (f.vertical) params.set('vertical', f.vertical);
  if (f.function) params.set('function', f.function);
  if (f.category) params.set('category', f.category);
  if (f.search) params.set('search', f.search);
  if (f.page) params.set('page', String(f.page));
  if (f.page_size) params.set('page_size', String(f.page_size));
  if (f.tenant_id) params.set('tenant_id', f.tenant_id);
  if (f.tags_any?.length) {
    f.tags_any.forEach((tag) => params.append('tags_any', tag));
  }
  if (f.tags_all?.length) {
    f.tags_all.forEach((tag) => params.append('tags_all', tag));
  }
  if (f.tags_none?.length) {
    f.tags_none.forEach((tag) => params.append('tags_none', tag));
  }
  const q = params.toString();
  return q ? `?${q}` : '';
}

// =============================================================================
// Admin scope hooks
// =============================================================================

export function useAdminSkills(filters: SkillListFilters = {}) {
  return useQuery({
    queryKey: [SKILLS_KEY, 'admin', filters],
    queryFn: async () => {
      const { data } = await apiClient.get<SkillListResponse>(`/api/v1/admin/skills${buildListQuery(filters)}`);
      return data;
    },
  });
}

/**
 * List skills visible to the current tenant: platform tiers + the tenant's
 * own tenant/byos skills. This is the catalog used by the agent scope DSL
 * (whitelist picker, bindings picker).
 */
export function useTenantSkillCatalog(filters: SkillListFilters = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [SKILLS_KEY, 'tenant-catalog', tenantId, filters],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<SkillListResponse>(
        `/api/v1/tenants/${tenantId}/skills${buildListQuery(filters)}`,
      );
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useAdminSkill(skillId: string | undefined, tenantId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: [SKILL_KEY, 'admin', skillId, tenantId],
    queryFn: async () => {
      const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      const { data } = await apiClient.get<Skill>(`/api/v1/admin/skills/${skillId}${q}`);
      return data;
    },
    // ISS-289: tenant-scoped (BYOS) skills resolve only when the tenant is
    // sent. Wait for the tenant context instead of firing a platform-scoped
    // request that 404s for tenant skills.
    enabled: !!skillId && enabled,
  });
}

export function useAdminSkillFacets(tenantId?: string) {
  const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  return useQuery({
    queryKey: [SKILLS_KEY, 'admin', 'facets', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<SkillFacets>(`/api/v1/admin/skills/facets${q}`);
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 min — facets change rarely
  });
}

export function useCreateAdminSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { payload: SkillCreate; tenant_id?: string }) => {
      const q = input.tenant_id ? `?tenant_id=${encodeURIComponent(input.tenant_id)}` : '';
      const { data } = await apiClient.post<Skill>(`/api/v1/admin/skills${q}`, input.payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

export function usePatchAdminSkill(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SkillUpdate) => {
      const { data } = await apiClient.patch<Skill>(`/api/v1/admin/skills/${skillId}`, payload);
      return data;
    },
    onSuccess: () => {
      // ISS-259 — invalidate by prefix: the detail query key uses the slug
      // (['skill','admin',<slug>,<tenantId>]) while mutations are keyed by id.
      qc.invalidateQueries({ queryKey: [SKILL_KEY, 'admin'] });
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

export function useTransitionAdminSkill(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SkillLifecycleTransition) => {
      const { data } = await apiClient.post<Skill>(`/api/v1/admin/skills/${skillId}/lifecycle`, payload);
      return data;
    },
    onSuccess: () => {
      // ISS-259 — invalidate by prefix: the detail query key uses the slug
      // (['skill','admin',<slug>,<tenantId>]) while mutations are keyed by id.
      qc.invalidateQueries({ queryKey: [SKILL_KEY, 'admin'] });
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

export function useDeleteAdminSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { skillId: string; hard?: boolean; confirm?: SkillHardDeleteRequest }) => {
      const path = `/api/v1/admin/skills/${input.skillId}${input.hard ? '?hard=true' : ''}`;
      // Use apiClient directly to send a body with DELETE.
      await apiClient.delete(path, input.hard ? { data: input.confirm } : undefined);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

export function useAdminSkillUsage(skillId: string | undefined) {
  return useQuery({
    queryKey: [SKILL_USAGE_KEY, 'admin', skillId],
    queryFn: async () => {
      const { data } = await apiClient.get<SkillUsageResponse>(`/api/v1/admin/skills/${skillId}/usage`);
      return data;
    },
    enabled: !!skillId,
  });
}

const SKILL_VERSIONS_KEY = 'skill-versions';

export function useAdminSkillVersions(skillId: string | undefined) {
  return useQuery({
    queryKey: [SKILL_VERSIONS_KEY, skillId],
    queryFn: async () => {
      const { data } = await apiClient.get<SkillVersionListResponse>(`/api/v1/admin/skills/${skillId}/versions`);
      return data;
    },
    enabled: !!skillId,
  });
}

export function useRollbackAdminSkill(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SkillRollbackRequest) => {
      const { data } = await apiClient.post(`/api/v1/admin/skills/${skillId}/rollback`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_VERSIONS_KEY, skillId] });
      qc.invalidateQueries({ queryKey: [SKILL_KEY, 'admin', skillId] });
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

export function useUploadAdminSkillAsset(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SkillAssetCreate) => {
      const { data } = await apiClient.post<SkillAsset>(`/api/v1/admin/skills/${skillId}/assets`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_KEY, 'admin', skillId] });
    },
  });
}

export function useDeleteAdminSkillAsset(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assetId: string) => {
      await apiClient.delete(`/api/v1/admin/skills/${skillId}/assets/${assetId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_KEY, 'admin', skillId] });
    },
  });
}

// =============================================================================
// Tenant scope hooks (for tenant_skill / byos_skill creation by tenant admins)
// =============================================================================

/**
 * Install a published platform skill into the current tenant.
 * Calls `POST /tenants/{tid}/skills/{slug}/install` which creates a
 * `tenant_skill` draft copy. Requires Tenant#admin (Keto).
 */
export function useInstallPlatformSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await apiClient.post<Skill>(`/api/v1/skills/${slug}/install`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
      qc.invalidateQueries({ queryKey: [SKILL_KEY] });
    },
  });
}

export function useTenantSkills(filters: SkillListFilters = {}) {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: [SKILLS_KEY, 'tenant', currentTenant?.id, filters],
    queryFn: async () => {
      const { data } = await apiClient.get<SkillListResponse>(`/api/v1/skills${buildListQuery(filters)}`);
      return data;
    },
    enabled: !!currentTenant?.id,
  });
}

export function useTenantSkill(skillId: string | undefined) {
  const { currentTenant } = useTenant();
  return useQuery({
    queryKey: [SKILL_KEY, 'tenant', currentTenant?.id, skillId],
    queryFn: async () => {
      const { data } = await apiClient.get<Skill>(`/api/v1/skills/${skillId}`);
      return data;
    },
    enabled: !!skillId && !!currentTenant?.id,
  });
}

export function useCreateTenantSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SkillCreate) => {
      const { data } = await apiClient.post<Skill>('/api/v1/skills', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

// =============================================================================
// Import (Sprint 132 Stream D — M-A9 wizard)
// =============================================================================

export interface SkillImportInput {
  file: File;
  format: SkillImportFormat;
  /** When true (default) the backend returns a preview only; nothing is
   *  persisted. Set false on the final wizard step. */
  dry_run: boolean;
  /** Optional override of the proposed_skill so the user can fix issues
   *  detected during dry-run before confirming. Only honoured by the
   *  wizard — the backend always re-parses the file, so we send the file
   *  on every call. */
  override?: Partial<SkillCreate>;
}

/**
 * Import a skill via the tenant-scoped `POST /tenants/{tid}/skills/import`
 * endpoint. Used by the wizard for both the dry-run preview step and the
 * final confirm step.
 */
export function useImportTenantSkill() {
  const qc = useQueryClient();
  return useMutation<SkillImportResponse, Error, SkillImportInput>({
    mutationFn: async ({ file, format, dry_run, override: _override }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('format', format);
      fd.append('dry_run', String(dry_run));
      // The backend re-parses the file each call. Inline overrides are
      // applied in the UI before display — the wizard only re-sends the
      // file when the user keeps the proposed skill as-is.
      const { data } = await apiClient.post<SkillImportResponse>(
        `/api/v1/skills/import`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: (resp) => {
      if (!resp.dry_run && resp.skill) {
        qc.invalidateQueries({ queryKey: [SKILLS_KEY] });
      }
    },
  });
}

export type { SkillImportFormat, SkillImportPreview, SkillImportResponse };

// =============================================================================
// Agent skill scope + bindings hooks
// =============================================================================

export function useAgentSkillScope(agentId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [AGENT_SCOPE_KEY, tenantId, agentId],
    queryFn: async () => {
      if (!tenantId || !agentId) throw new Error('No tenant selected');
      // NOTE: the endpoint returns the scope object directly (no {scope} wrapper).
      const { data } = await apiClient.get<SkillScope>(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills/scope`,
      );
      return data;
    },
    enabled: !!agentId && !!tenantId,
  });
}

export function useUpdateAgentSkillScope(agentId: string) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (payload: AgentSkillsScopeUpdate) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      // NOTE: the endpoint returns the scope object directly (no {scope} wrapper).
      const { data } = await apiClient.put<SkillScope>(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills/scope`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [AGENT_SCOPE_KEY, agentId] });
      qc.invalidateQueries({ queryKey: [AGENT_PREVIEW_KEY, agentId] });
    },
  });
}

/**
 * Imperative preview helper — used with a debounce inside the component.
 */
export function useAgentScopePreview(agentId: string) {
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (payload: AgentSkillsScopeUpdate) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<SkillScopePreviewResponse>(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills/preview`,
        payload,
      );
      return data;
    },
  });
}

export function useAgentSkillBindings(agentId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [AGENT_BINDINGS_KEY, tenantId, agentId],
    queryFn: async () => {
      if (!tenantId || !agentId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<AgentSkillBinding[]>(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills`,
      );
      return data;
    },
    enabled: !!agentId && !!tenantId,
  });
}

export function useUpsertAgentSkillBinding(agentId: string) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (payload: AgentSkillBindingCreate) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<AgentSkillBinding>(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [AGENT_BINDINGS_KEY, agentId] });
    },
  });
}

export function usePatchAgentSkillBinding(agentId: string) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (input: { skillId: string; payload: AgentSkillBindingPatch }) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.patch<AgentSkillBinding>(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills/${input.skillId}`,
        input.payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [AGENT_BINDINGS_KEY, agentId] });
    },
  });
}

export function useDeleteAgentSkillBinding(agentId: string) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (skillId: string) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      await apiClient.delete(
        `/api/v1/tenants/${tenantId}/agents/${agentId}/skills/${skillId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [AGENT_BINDINGS_KEY, agentId] });
    },
  });
}

// ============================================================
// Sprint 133 Stream C — M-A12: Skill editors (admin)
// ============================================================
const SKILL_EDITORS_KEY = 'skill-editors';

export function useSkillEditors(skillId: string | undefined) {
  return useQuery({
    queryKey: [SKILL_EDITORS_KEY, skillId],
    queryFn: async () => {
      const { data } = await apiClient.get<import('../types').SkillEditorListResponse>(
        `/api/v1/admin/skills/${skillId}/editors`,
      );
      return data;
    },
    enabled: Boolean(skillId),
  });
}

export function useAddSkillEditor(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await apiClient.post<import('../types').SkillEditor>(
        `/api/v1/admin/skills/${skillId}/editors`,
        { user_id: userId },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_EDITORS_KEY, skillId] });
    },
  });
}

export function useRemoveSkillEditor(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/api/v1/admin/skills/${skillId}/editors/${userId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_EDITORS_KEY, skillId] });
    },
  });
}

// ============================================================
// Sprint 133 Stream C — M-A13: Skill group grants / restrictions (admin)
// ============================================================
const SKILL_GROUP_GRANTS_KEY = 'skill-group-grants';

export function useSkillGroupGrants(skillId: string | undefined) {
  return useQuery({
    queryKey: [SKILL_GROUP_GRANTS_KEY, skillId],
    queryFn: async () => {
      const { data } = await apiClient.get<import('../types').SkillGroupGrantListResponse>(
        `/api/v1/admin/skills/${skillId}/group-grants`,
      );
      return data;
    },
    enabled: Boolean(skillId),
  });
}

export function useAddSkillGroupGrant(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: import('../types').SkillGroupGrantAddRequest) => {
      const { data } = await apiClient.post<import('../types').SkillGroupGrant>(
        `/api/v1/admin/skills/${skillId}/group-grants`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_GROUP_GRANTS_KEY, skillId] });
    },
  });
}

export function useRemoveSkillGroupGrant(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { group_id: string; relation: import('../types').SkillGroupRelation }) => {
      await apiClient.delete(
        `/api/v1/admin/skills/${skillId}/group-grants/${input.relation}/${input.group_id}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SKILL_GROUP_GRANTS_KEY, skillId] });
    },
  });
}
